import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Plus, X, Image as ImageIcon } from 'lucide-react';
import { useDataManager } from '../../hooks/useDataManager';
import ImagePicker from '../../components/ImagePicker';

const ProductForm = ({ product, onClose }) => {
  const { categories, addProduct, updateProduct } = useDataManager();

  // Sort categories by sequence
  const sortedCategories = [...categories].sort((a, b) => {
    const seqA = a.sequence ?? a.id;
    const seqB = b.sequence ?? b.id;
    return seqA - seqB;
  });

  const emptyForm = {
    sku: '',
    name: '',
    price: '',
    category: '',
    description: '',
    fabric: '',
    careInstructions: '',
    sizes: [
      { size: 'S', available: true },
      { size: 'M', available: true },
      { size: 'L', available: true },
      { size: 'XL', available: true },
    ],
    colors: [{ name: '', hex: '#000000', stock: 0 }],
    images: [''],
    stock: '',
    featured: true,
    newArrival: false,
    saleEnabled: true,
    salePercentage: '',
    tags: '',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

  useEffect(() => {
    if (product) {
      const tags = product.tags ? product.tags.join(', ') : '';
      // Ensure category is set correctly - it should be the slug
      const category = product.category || '';
      setFormData({ ...product, tags, category });
      console.log('📝 Loading product for edit:', {
        productId: product.id,
        productName: product.name,
        productCategory: product.category,
        categoryType: typeof product.category,
        availableCategories: categories.map(c => ({ name: c.name, slug: c.slug }))
      });
    }
  }, [product, categories]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const tagsArray = formData.tags ? formData.tags.split(',').map(t => t.trim()) : [];
    const filteredImages = formData.images.filter(img => img.trim() !== '');
    const parsedColors = formData.colors.map(c => ({
      ...c,
      stock: parseInt(c.stock) || 0
    }));

    const productData = {
      ...formData,
      price: parseInt(formData.price),
      stock: parseInt(formData.stock),
      colors: parsedColors,
      images: filteredImages,
      tags: tagsArray,
      category: formData.category, // Explicitly include category
      salePercentage: formData.salePercentage ? parseInt(formData.salePercentage) : null
    };

    console.log('💾 Submitting product:', {
      productId: product?.id,
      category: formData.category,
      allData: productData
    });

    if (product) {
      updateProduct(product.id, productData);
    } else {
      addProduct(productData);
    }

    onClose();
  };

  // Show loading if categories aren't loaded yet
  if (!categories || categories.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Loading categories...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>SKU *</Label>
          <Input
            value={formData.sku}
            onChange={(e) => setFormData({...formData, sku: e.target.value})}
            required
          />
        </div>
        <div>
          <Label>Product Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Price (PKR) *</Label>
          <Input
            type="number"
            value={formData.price}
            onChange={(e) => setFormData({...formData, price: e.target.value})}
            required
          />
        </div>
        <div>
          <Label>Stock *</Label>
          <Input
            type="number"
            value={formData.stock}
            onChange={(e) => setFormData({...formData, stock: e.target.value})}
            required
          />
        </div>
        <div>
          <Label>Category *</Label>
          <Select
            key={product?.id || 'new'}
            value={formData.category || ''}
            onValueChange={(value) => {
              console.log('🔄 Category changed to:', value);
              setFormData({...formData, category: value});
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {sortedCategories.map(cat => (
                <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Debug info */}
          <p className="text-xs text-gray-500 mt-1">
            Current value: <code className="bg-gray-100 px-1 rounded">{formData.category || 'none'}</code>
          </p>
        </div>
      </div>

      <div>
        <Label>Description *</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fabric *</Label>
          <Input
            value={formData.fabric}
            onChange={(e) => setFormData({...formData, fabric: e.target.value})}
            required
          />
        </div>
        <div>
          <Label>Care Instructions *</Label>
          <Input
            value={formData.careInstructions}
            onChange={(e) => setFormData({...formData, careInstructions: e.target.value})}
            required
          />
        </div>
      </div>

      <div>
        <Label>Product Images *</Label>
        <div className="space-y-3">
          {/* Image Preview Grid */}
          {formData.images.filter(img => img).length > 0 && (
            <div className="grid grid-cols-4 gap-3">
              {formData.images.filter(img => img).map((image, index) => (
                <div key={index} className="relative border rounded-lg p-2">
                  <img
                    src={image}
                    alt={`Product ${index + 1}`}
                    className="h-24 w-24 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newImages = formData.images.filter((_, i) => i !== index);
                      setFormData({...formData, images: newImages.length > 0 ? newImages : ['']});
                    }}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                  >
                    <X size={14} />
                  </button>
                  {index === 0 && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-[#4a1a1f] text-white text-xs px-2 py-0.5 rounded">
                      Main
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsImagePickerOpen(true)}
              className="w-full"
            >
              <ImageIcon size={18} className="mr-2" />
              {formData.images.filter(img => img).length > 0 ? 'Add/Change Images' : 'Select Images from Media Bank'}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label>Tags (comma separated)</Label>
        <Input
          value={formData.tags}
          onChange={(e) => setFormData({...formData, tags: e.target.value})}
          placeholder="bestseller, classic"
        />
      </div>

      <div className="space-y-4 border-t pt-4">
        <div className="flex items-center space-x-2">
          <Switch
            checked={formData.featured}
            onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
          />
          <Label>Featured Product</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            checked={formData.newArrival}
            onCheckedChange={(checked) => setFormData({...formData, newArrival: checked})}
          />
          <Label>New Arrival</Label>
        </div>

        <div className="space-y-3 border-l-4 border-[#4a1a1f] pl-4 bg-gray-50 p-4 rounded">
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.saleEnabled}
              onCheckedChange={(checked) => setFormData({...formData, saleEnabled: checked})}
            />
            <Label>Sale Applicable on This Product</Label>
          </div>

          <div>
            <Label>Product-Specific Sale Percentage (Optional)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.salePercentage}
              onChange={(e) => setFormData({...formData, salePercentage: e.target.value})}
              placeholder="Leave empty to use global sale percentage"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              If filled, this percentage will override the global sale setting. If empty, the global sale percentage will be used when global sale is active.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" className="bg-[#4a1a1f] hover:bg-[#5a2a2f]">
          {product ? 'Update' : 'Add'} Product
        </Button>
      </div>

      {/* Image Picker Modal */}
      <ImagePicker
        isOpen={isImagePickerOpen}
        onClose={() => setIsImagePickerOpen(false)}
        onSelect={(selectedImages) => {
          setFormData({...formData, images: selectedImages});
        }}
        selectedUrl={null}
        multiple={true}
      />
    </form>
  );
};

export default ProductForm;
